import React, {useEffect, useMemo} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBinoculars,
    faCalendarDays,
    faChevronLeft,
    faCircleCheck,
    faClock,
    faCrow,
    faLocationDot,
    faSkullCrossbones
} from "@fortawesome/free-solid-svg-icons";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdObservationsBySpecies} from "ebird-mydata-reader";
import {useLocation, useNavigate} from "react-router-dom";
import {getFamilySlug} from "../../helpers/helpers";
import {getRandomDashboardHeroImage} from "../../helpers/dashboardHeroImages";
import {toRootRelativeImagePath} from "../../helpers/imagePaths";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../components/explorer-hero";
import {FamilyList} from "./family-list";
import {FamilyDetail} from "./family-detail";
import {SpeciesDetail, getSpeciesObservations, getSpeciesStats} from "./species-detail";
import {
    getFamilyCommonName,
    getFamilyScientificName,
    getFamilyStats,
    getShortDate,
    getSpeciesStatusLabel
} from "./helpers";
import './family-explorer.scss';

export type FamilyExplorerProps = {
    checklist: EBirdChecklistByFamily[],
    observationsBySpecies: EBirdObservationsBySpecies[]
}

const FAMILY_EXPLORER_ROUTE = '/family-explorer';
const LEGACY_CHECKLIST_ROUTE = '/checklist';

export default function FamilyExplorer({checklist, observationsBySpecies}: FamilyExplorerProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const familyPathPrefix = location.pathname.startsWith(`${LEGACY_CHECKLIST_ROUTE}/family/`)
        ? `${LEGACY_CHECKLIST_ROUTE}/family/`
        : `${FAMILY_EXPLORER_ROUTE}/family/`;
    const familySlug = location.pathname.startsWith(`${FAMILY_EXPLORER_ROUTE}/family/`) || location.pathname.startsWith(`${LEGACY_CHECKLIST_ROUTE}/family/`)
        ? decodeURIComponent(location.pathname.replace(familyPathPrefix, '').split('/')[0])
        : undefined;
    const speciesCode = location.pathname.includes('/species/')
        ? decodeURIComponent(location.pathname.split('/species/')[1]?.split('/')[0] ?? '')
        : undefined;

    useEffect(() => {
        if (!location.pathname.startsWith(LEGACY_CHECKLIST_ROUTE)) return;

        navigate(location.pathname.replace(LEGACY_CHECKLIST_ROUTE, FAMILY_EXPLORER_ROUTE), {replace: true});
    }, [location.pathname, navigate]);
    const selectedFamily = useMemo(() => {
        if (!familySlug) return null;
        return checklist.find(family => getFamilySlug(family.familyName) === familySlug) ?? null;
    }, [checklist, familySlug]);
    const selectedSpecies = useMemo(() => {
        if (!selectedFamily || !speciesCode) return null;

        return selectedFamily.speciesList.find(species => {
            return species.speciesCode === speciesCode || species.alternateSpeciesCodes?.includes(speciesCode);
        }) ?? null;
    }, [selectedFamily, speciesCode]);
    const checklistHeroImage = useMemo(() => {
        return getRandomDashboardHeroImage();
    }, []);
    const selectedFamilyHeroImage = useMemo(() => {
        if (!selectedFamily) return undefined;

        const familyImage = selectedSpecies?.image ?? selectedFamily.speciesList[0]?.image;
        return toRootRelativeImagePath(familyImage?.medium ?? familyImage?.thumb);
    }, [selectedFamily, selectedSpecies]);
    const heroImage = selectedFamilyHeroImage ?? checklistHeroImage;
    const selectedFamilyStats = useMemo(() => {
        return selectedFamily ? getFamilyStats(selectedFamily) : null;
    }, [selectedFamily]);
    const selectedSpeciesStats = useMemo(() => {
        if (!selectedSpecies) return null;

        return getSpeciesStats(
            selectedSpecies,
            getSpeciesObservations(selectedSpecies, observationsBySpecies)
        );
    }, [observationsBySpecies, selectedSpecies]);
    const checklistStats = useMemo(() => {
        const familyStats = checklist.map(getFamilyStats);
        const seenSpecies = familyStats.reduce((total, stats) => total + stats.seenSpecies, 0);
        const totalSpecies = familyStats.reduce((total, stats) => total + stats.totalSpecies, 0);
        const years = new Set<number>();

        for (const family of checklist) {
            for (const species of family.speciesList) {
                if (!species.seen || !species.firstObservation?.date) continue;

                const year = parseInt(species.firstObservation.date.split('-')[0]);
                if (!Number.isNaN(year)) {
                    years.add(year);
                }
            }
        }

        return {
            seenSpecies,
            totalSpecies,
            unseenSpecies: Math.max(totalSpecies - seenSpecies, 0),
            seenFamilies: checklist.filter(family => {
                const stats = getFamilyStats(family);
                return !stats.isExtinct && family.seen;
            }).length,
            unseenFamilies: checklist.filter(family => {
                const stats = getFamilyStats(family);
                return !stats.isExtinct && !family.seen;
            }).length,
            extinctFamilies: familyStats.filter(stats => stats.isExtinct).length,
            years: years.size
        };
    }, [checklist]);

    const handleChangeFamily = (family: EBirdChecklistByFamily) => {
        navigate(`${FAMILY_EXPLORER_ROUTE}/family/${getFamilySlug(family.familyName)}`);
    }

    const handleShowAllFamilies = () => {
        navigate(FAMILY_EXPLORER_ROUTE);
    }

    const handleShowFamily = () => {
        if (!selectedFamily) return;

        navigate(`${FAMILY_EXPLORER_ROUTE}/family/${getFamilySlug(selectedFamily.familyName)}`);
    }

    const handleChangeSpecies = (species: EBirdChecklistBySpecies) => {
        if (!selectedFamily) return;

        navigate(`${FAMILY_EXPLORER_ROUTE}/family/${getFamilySlug(selectedFamily.familyName)}/species/${species.speciesCode}`);
    }

    const renderSpeciesStatus = (species: EBirdChecklistBySpecies) => (
        <span className={`checklist__species-status ${species.isExtinct ? 'checklist__species-status--extinct' : species.seen ? 'checklist__species-status--seen' : ''}`}>
            <FontAwesomeIcon icon={species.isExtinct ? faSkullCrossbones : species.seen ? faCircleCheck : faCrow} aria-hidden="true" />
            {getSpeciesStatusLabel(species)}
        </span>
    );

    const renderMetric = (icon: any, label: string, value: string|number, helper?: string) => (
        <div className="checklist__species-metric">
            <span className="checklist__species-metric-icon" aria-hidden="true">
                <FontAwesomeIcon icon={icon} />
            </span>
            <span>
                <span>{label}</span>
                <strong>{value}</strong>
                {helper ? <small>{helper}</small> : null}
            </span>
        </div>
    );

    const renderHeroBack = () => {
        if (selectedSpecies && selectedFamily) {
            return (
                <button type="button" className="checklist__hero-back" onClick={handleShowFamily}>
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    {getFamilyScientificName(selectedFamily)} ({getFamilyCommonName(selectedFamily)})
                </button>
            );
        }

        if (selectedFamily) {
            return (
                <button type="button" className="checklist__hero-back" onClick={handleShowAllFamilies}>
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    All Families
                </button>
            );
        }

        return null;
    }

    const getHeroEyebrow = () => {
        if (selectedSpecies) return 'Species Detail';
        if (selectedFamily) return 'Family Detail';
        return 'Family Explorer';
    }

    const renderHeroStats = () => {
        if (selectedSpecies) {
            if (!selectedSpecies.seen || !selectedSpeciesStats) return null;

            return (
                <ExplorerHeroStats ariaLabel="Species summary">
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faCalendarDays} />} label="First Seen" value={getShortDate(selectedSpeciesStats.firstObservation?.date) || '--'} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faClock} />} label="Most Recent" value={getShortDate(selectedSpeciesStats.latestObservation?.date) || '--'} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faBinoculars} />} label="Total Observations" value={selectedSpeciesStats.observationCount.toLocaleString()} />
                    <ExplorerHeroStat icon={<FontAwesomeIcon icon={faLocationDot} />} label="Unique Locations" value={selectedSpeciesStats.locationCount.toLocaleString()} />
                </ExplorerHeroStats>
            );
        }

        return (
            <ExplorerHeroStats ariaLabel="Checklist summary">
                <ExplorerHeroStat
                    icon={<FontAwesomeIcon icon={faBinoculars} />}
                    label="Species Seen"
                    value={(selectedFamilyStats?.seenSpecies ?? checklistStats.seenSpecies).toLocaleString()}
                />
                {selectedFamily ?
                    <>
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faCrow} />}
                            label="Species Seen"
                            value={`${(selectedFamilyStats?.seenPercent ?? 0).toLocaleString()}%`}
                        />
                    </> :
                    <>
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faCrow} />}
                            label="Total Species"
                            value={checklistStats.totalSpecies.toLocaleString()}
                        />
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faCalendarDays} />}
                            label={checklistStats.years === 1 ? 'Year Birding' : 'Years Birding'}
                            value={checklistStats.years.toLocaleString()}
                        />
                    </>
                }
            </ExplorerHeroStats>
        );
    }

    const renderChecklistView = () => {
        if (selectedFamily && selectedSpecies) {
            return (
                <SpeciesDetail
                    family={selectedFamily}
                    species={selectedSpecies}
                    observationsBySpecies={observationsBySpecies}
                />
            );
        }

        if (selectedFamily) {
            return (
                <FamilyDetail
                    family={selectedFamily}
                    observationsBySpecies={observationsBySpecies}
                    onChangeSpecies={handleChangeSpecies}
                />
            );
        }

        return (
            <FamilyList
                checklist={checklist}
                checklistStats={checklistStats}
                onChangeFamily={handleChangeFamily}
            />
        );
    }

    return (
        <section
            className="checklist"
            aria-labelledby="checklist-title"
            style={{'--checklist-hero-image': `url("${heroImage}")`} as React.CSSProperties}
        >
            <div className={`checklist__hero ${selectedFamily ? 'checklist__hero--family' : ''} ${selectedSpecies ? 'checklist__hero--species' : ''}`}>
                <div className="checklist__hero-content">
                    {renderHeroBack()}
                    <p className="checklist__eyebrow">{getHeroEyebrow()}</p>
                    <h1 id="checklist-title" className="checklist__title">
                        {selectedSpecies ? selectedSpecies.commonName : selectedFamily ? getFamilyScientificName(selectedFamily) : 'See the Big Picture'}
                    </h1>
                    <p className="checklist__intro">
                        {selectedSpecies ?
                            selectedSpecies.scientificName :
                            selectedFamily ?
                            getFamilyCommonName(selectedFamily) :
                            'Explore bird families and check what you’ve seen so far.'
                        }
                    </p>
                    {selectedSpecies ? renderSpeciesStatus(selectedSpecies) : null}
                    {renderHeroStats()}
                </div>
            </div>

            <div className="checklist__panel">
                {renderChecklistView()}
            </div>
        </section>
    );
}
